"use client"

import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Button } from "../../../components/ui/button"
import { Switch } from "../../../components/ui/switch"
import { Label } from "../../../components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Bell, Mail, MessageSquare } from "lucide-react"

const systemNotifications = [
  {
    id: "new-user-registration",
    title: "New User Registration",
    description: "Notify admins when new users register",
    enabled: true,
    channels: ["email", "dashboard"],
  },
  // {
  //   id: "prayer-request-submitted",
  //   title: "Prayer Request Submitted",
  //   description: "Alert when new prayer requests are submitted",
  //   enabled: true,
  //   channels: ["email", "sms", "dashboard"],
  // },
  {
    id: "comment-pending-approval",
    title: "Comment Pending Approval",
    description: "Notify when comments need moderation",
    enabled: true,
    channels: ["email", "dashboard"],
  },
  // {
  //   id: "donation-received",
  //   title: "Donation Received",
  //   description: "Alert when donations are processed",
  //   enabled: false,
  //   channels: ["email"],
  // },
  {
    id: "event-registration",
    title: "Event Registration",
    description: "Notify when users register for events",
    enabled: true,
    channels: ["email", "dashboard"],
  },
]

const portalNotifications = [
  {
    id: "youth-camp-registration",
    title: "Youth Camp Registration",
    description: "Send confirmation emails to youth camp registrants",
    enabled: true,
    autoReply: true,
  },
  {
    id: "volunteer-application",
    title: "Volunteer Application",
    description: "Acknowledge volunteer applications",
    enabled: true,
    autoReply: true,
  },
  {
    id: "prayer-request-confirmation",
    title: "Prayer Request Confirmation",
    description: "Confirm receipt of prayer requests",
    enabled: true,
    autoReply: false,
  },
]

export function NotificationsSection({allUsers,adminUsers,comments}:any) {
  const [systemSettings, setSystemSettings] = useState(systemNotifications)
  const [portalSettings, setPortalSettings] = useState([])

  const toggleSystemNotification = (id: string) => {
    setSystemSettings((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, enabled: !notification.enabled } : notification,
      ),
    )
  }

  const togglePortalNotification = (id: string) => {
    setPortalSettings((prev:any) =>
      prev.map((notification:any) =>
        notification.id === id ? { ...notification, enabled: !notification.enabled } : notification,
      ),
    )
  }

  const toggleAutoReply = (id: string) => {
    setPortalSettings((prev:any) =>
      prev.map((notification:any) =>
        notification.id === id ? { ...notification, autoReply: !notification.autoReply } : notification,
      ),
    )
  }

  const saveSettings = () => {
    console.log("Saving notification settings:", { systemSettings, portalSettings })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Settings
          </CardTitle>
          <CardDescription>Configure system-wide notifications and portal-based alerts</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="system" className="space-y-4">
            <TabsList>
              <TabsTrigger value="system">System Notifications</TabsTrigger>
              <TabsTrigger value="portal">Portal Notifications</TabsTrigger>
              <TabsTrigger value="admin">Admin Alerts</TabsTrigger>
            </TabsList>

            <TabsContent value="system" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">System-wide Notifications</h3>
                <p className="text-sm text-muted-foreground">
                  Configure notifications for administrative events and user actions
                </p>

                <div className="space-y-4">
                  {systemSettings.map((notification) => (
                    <Card key={notification.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{notification.title}</h4>
                              <Switch
                                checked={notification.enabled}
                                onCheckedChange={() => toggleSystemNotification(notification.id)}
                              />
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{notification.description}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Channels:</span>
                              {notification.channels.map((channel) => (
                                <span
                                  key={channel}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs"
                                >
                                  {channel === "email" && <Mail className="h-3 w-3" />}
                                  {channel === "sms" && <MessageSquare className="h-3 w-3" />}
                                  {channel === "dashboard" && <Bell className="h-3 w-3" />}
                                  {channel}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="portal" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Portal-based Notifications</h3>
                <p className="text-sm text-muted-foreground">
                  Configure automatic responses and confirmations for form submissions
                </p>

                <div className="space-y-4">
                  {portalSettings.map((notification:any) => (
                    <Card key={notification.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{notification.title}</h4>
                              <Switch
                                checked={notification.enabled}
                                onCheckedChange={() => togglePortalNotification(notification.id)}
                              />
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">{notification.description}</p>
                            <div className="flex items-center gap-4">
                              <Label className="flex items-center gap-2 text-sm">
                                <Switch
                                  checked={notification.autoReply}
                                  onCheckedChange={() => toggleAutoReply(notification.id)}
                                  disabled={!notification.enabled}
                                  // size="sm"
                                />
                                Auto-reply enabled
                              </Label>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="admin" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Admin-specific Alerts</h3>
                <p className="text-sm text-muted-foreground">
                  Configure personal notification preferences for administrators
                </p>

                <Card>
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Email Digest</h4>
                        <p className="text-sm text-muted-foreground">Receive daily summary of activities</p>
                      </div>
                      <Switch defaultChecked />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Real-time Alerts</h4>
                        <p className="text-sm text-muted-foreground">Immediate notifications for urgent items</p>
                      </div>
                      <Switch defaultChecked />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Mobile Push Notifications</h4>
                        <p className="text-sm text-muted-foreground">Send alerts to mobile devices</p>
                      </div>
                      <Switch />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>

          <div className="pt-6 border-t">
            <Button onClick={saveSettings}>Save Notification Settings</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
